"use client";

import { ArrowRight, File, FileImage, FileText, Shield, Sparkles, Upload, Users, Zap } from "lucide-react";
import { useRouter } from "next/navigation";
import { useState } from "react";
import { createDocument } from "@/components/scriba/document/service/basic";
import { ProgressivePage } from "@/components/scriba/ui/ProgressivePage";
import { Button } from "@/components/tailwind/ui/button";

export default function HomePage() {
  const router = useRouter();
  const [isCreatingDocument, setIsCreatingDocument] = useState(false);

  const features = [
    {
      icon: <Upload className="h-6 w-6" />,
      title: "Upload & Process",
      description: "Upload PDFs, images, or capture documents with your camera for instant text extraction.",
    },
    {
      icon: <Sparkles className="h-6 w-6" />,
      title: "AI-Powered OCR",
      description: "Advanced OCR with Google Cloud Vision and Tesseract for accurate text recognition.",
    },
    {
      icon: <FileText className="h-6 w-6" />,
      title: "Rich Text Editor",
      description: "Edit and format extracted text with our powerful rich text editor.",
    },
    {
      icon: <Zap className="h-6 w-6" />,
      title: "Smart Processing",
      description: "Intelligent text cleaning and enhancement with AI assistance.",
    },
    {
      icon: <Users className="h-6 w-6" />,
      title: "Collaboration",
      description: "Share and collaborate on documents with team members.",
    },
    {
      icon: <Shield className="h-6 w-6" />,
      title: "Secure & Private",
      description: "Your documents are processed securely and never stored permanently.",
    },
  ];

  const supportedFormats = [
    { icon: <File className="h-5 w-5" />, name: "PDF Files" },
    { icon: <FileImage className="h-5 w-5" />, name: "Images (JPG, PNG, etc.)" },
  ];

  const sections = [
    {
      id: "homepage-cta",
      content: (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center w-full">
            <p className="text-xl text-gray-600 dark:text-gray-300 mb-8 max-w-3xl mx-auto">
              The last editor you&apos;ll ever need. Edit your documents, extract text, add images, and more.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={async () => {
                  setIsCreatingDocument(true);
                  try {
                    // Create a new document (temporary if not signed in)
                    const newDoc = await createDocument({
                      titleOrOptions: {
                        title: "New Document",
                        isTemporary: true, // Will be checked on the backend based on auth status
                      },
                    });
                    router.push(`/documents/${newDoc.id}`);
                  } catch (error) {
                    console.error("Error creating document:", error);
                    setIsCreatingDocument(false);
                  }
                }}
                disabled={isCreatingDocument}
                size="lg"
                className="bg-gray-900 hover:bg-gray-800 dark:bg-white dark:hover:bg-gray-100 text-white dark:text-gray-900 px-8 py-3 text-lg disabled:opacity-50"
              >
                {isCreatingDocument ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-current mr-2" />
                    Creating...
                  </>
                ) : (
                  <>
                    Get Started
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </>
                )}
              </Button>

              <Button variant="outline" size="lg" onClick={() => router.push("/help")} className="px-8 py-3 text-lg">
                Learn More
              </Button>
            </div>
          </div>
        </div>
      ),
    },
    {
      id: "homepage-features",
      content: (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">Powerful Features</h2>
            <p className="text-lg text-gray-600 dark:text-gray-300">
              Everything you need to process and edit your documents
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature) => (
              <div
                key={feature.title}
                className="p-6 bg-gray-50 dark:bg-card rounded-lg hover:shadow-lg transition-shadow duration-300"
              >
                <div className="flex items-center mb-4">
                  <div className="p-2 bg-gray-100 dark:bg-muted rounded-lg mr-4">
                    <div className="text-gray-600 dark:text-gray-400">{feature.icon}</div>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{feature.title}</h3>
                </div>
                <p className="text-gray-600 dark:text-gray-300">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      ),
    },
    {
      id: "homepage-formats",
      content: (
        <div className="py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">Supported Formats</h2>
              <p className="text-lg text-gray-600 dark:text-gray-300">
                Upload any of these file types for text extraction
              </p>
            </div>

            <div className="flex flex-col sm:flex-row justify-center gap-8">
              {supportedFormats.map((format) => (
                <div key={format.name} className="flex items-center p-4 bg-gray-50 dark:bg-card rounded-lg shadow-sm">
                  <div className="text-gray-600 dark:text-gray-300 mr-3">{format.icon}</div>
                  <span className="text-gray-900 dark:text-white font-medium">{format.name}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      ),
    },
  ];

  return (
    <ProgressivePage
      pageWrapperProps={{ pageType: "home" }}
      header={{
        text: "Welcome to Scriba!",
        fontSize: 56,
        duration: 3000,
        colors: { light: "#111827", dark: "#ffffff" },
        className: "mb-4",
      }}
      sections={sections}
      contentWrapperClassName="flex flex-col gap-24"
      firstSectionDelay={200}
      sectionDelay={700}
    />
  );
}
